import dispatchTrackingEvent, {
  productToGtmItemListPayload
} from '@/widgets/services/TrackingService.ts';
import type {
  Filter,
  FilterableAttributeKey,
  FilterableValue,
  FilterDefinition,
  FilterOption
} from '@/domain/FilteringAndSorting';
import { hasSubscriptions, type Product } from '@/domain/Product';
import { PRICE_REPRESENTATION_MULTIPLIER } from '@/domain/Pricing';

export type FilterTrackingType = 'technology' | FilterableAttributeKey;

const PREFIXES = [
  'capsule-cupSize-',
  'capsule-range-label-',
  'capsule-range-',
  'capsule-aromatic-',
  'accessory-usage-'
];
export const FILTERED_LIST_ITEM_NAME = 'filtered_plp';
export const FAVORITE_LIST = 'wishlist_page';
export const FAVORITE_OOS_ID = 'wishlist_page_oos_alternative';
export const FAVORITE_OOS_NAME = 'wishlist_oos_alternative';

type ClickLocationOnClean =
  | 'clear chip'
  | 'clear group chip'
  | 'clear all chip'
  | 'clear all'
  | 'clear all ext';

type ClickLocationOnApply =
  | Lowercase<FilterDefinition['type']>
  | `${Lowercase<FilterDefinition['type']>}|${Lowercase<FilterDefinition['type']>}`
  | `${Lowercase<FilterDefinition['type']>},${Lowercase<FilterDefinition['type']>}`;

export const RAISED_BY = 'global_plp_v3';
export const CREATIVE_SLOT = 'global_plp_v3_promo_card';

const ALT_ITEM_LIST_ID = 'global_plp_v3_oos_alternative';
const FILTER_EVENT_NAME = 'plp_filter';
const MAX_TRACKING_LENGTH = 99;

export function trackFiltersRemoval(
  selections: [filter: Filter, filterOption: FilterOption][],
  click_location: ClickLocationOnClean
) {
  const { filterType, filterValues } = computeFilterTypeAndValues(selections);

  dispatchTrackingEvent({
    event: 'plp_filter',
    action_type: 'filter clear',
    ...normalizeFilterTypeAndValues(filterType, filterValues),
    click_location,
    event_raised_by: RAISED_BY
  });
}

export function getTrackFilterType(filter: Filter) {
  return `${filter.key}-${filter.label}`;
}

export function trackFilterAllOpened() {
  trackFilterOpened('all filters');
}

export function trackFilterDropdownOpened(filter: Filter) {
  trackFilterOpened(getTrackFilterType(filter), filter.type?.toLowerCase());
}

function trackFilterOpened(filterType: string, clickLocation?: string) {
  dispatchTrackingEvent({
    event: FILTER_EVENT_NAME,
    action_type: 'filter open',
    filter_type: filterType,
    click_location: clickLocation,
    event_raised_by: RAISED_BY
  });
}

export function trackFiltersApplied(filters: [Filter, FilterOption][]) {
  const { filterType, filterValues, clickLocation } =
    computeFilterTypeAndValues(filters);
  trackFilterApplied(filterType, filterValues, clickLocation);
}

export function trackSortingApplied(
  sortOptionId: string,
  filterType: string,
  isApplied: boolean
) {
  const tracking = {
    event: FILTER_EVENT_NAME,
    action_type: isApplied ? 'filter apply' : 'filter clear',
    click_location: 'automatic',
    filter_type: filterType,
    filter_values: sortOptionId,
    event_raised_by: RAISED_BY
  };

  dispatchTrackingEvent(tracking);
}

export function trackFilterApplied(
  filterType: string,
  filterValues: string,
  clickLocation: ClickLocationOnApply
) {
  dispatchTrackingEvent({
    event: FILTER_EVENT_NAME,
    action_type: 'filter apply',
    ...normalizeFilterTypeAndValues(filterType, filterValues),
    event_raised_by: RAISED_BY,
    click_location: clickLocation
  });
}

export function trackPLPPromotionCardEvent(data: {
  promotionId: string;
  promotionName: string;
  creativeSlot: string;
  creativeName: string;
}) {
  const { promotionId, promotionName, creativeSlot, creativeName } = data;
  dispatchTrackingEvent({
    event: 'view_promotion',
    event_raised_by: RAISED_BY,
    creative_slot: CREATIVE_SLOT,
    ecommerce: {
      promotion_id: promotionId,
      promotion_name: promotionName,
      creative_slot: creativeSlot,
      creative_name: creativeName
    }
  });
}

function computeFilterTypeAndValues(filters: [Filter, FilterOption][]) {
  const tracking: Record<string, string> = {};
  const clickLocations: Record<string, string> = {};

  for (const [category, filter] of filters) {
    const filterType = `${category.key}-${category.label}`;
    const location = (category as FilterDefinition).type.toLowerCase();

    if (!tracking[filterType]) {
      tracking[filterType] = normalizeFilterValues(filter.values);
      clickLocations[filterType] = location;
    } else {
      tracking[filterType] += `,${normalizeFilterValues(filter.values)}`;
    }
  }

  return {
    filterType: Object.keys(tracking).join('|'),
    filterValues: Object.values(tracking).join('|'),
    clickLocation: Object.values(clickLocations).join(
      '|'
    ) as ClickLocationOnApply
  };
}

export function trackProductGroupImpression(
  products: Product[],
  currency: string,
  trackingPosition: number,
  itemListName?: string,
  isDiscoveryOffer?: boolean,
  raisedBy = RAISED_BY
) {
  dispatchListItemTrackingEvent({
    itemListId: raisedBy,
    itemListName: itemListName || raisedBy,
    products,
    currency,
    isDiscoveryOffer: Boolean(isDiscoveryOffer),
    trackingPosition,
    raisedBy
  });
}

export function trackAlternativeImpression(
  alternativeProducts: Product[],
  outOfStockProduct: Product,
  currency: string,
  isDiscoveryOffer: boolean,
  trackingPosition: number,
  raisedBy = RAISED_BY
) {
  const isFavorite = raisedBy === FAVORITE_LIST;
  const id = isFavorite ? FAVORITE_OOS_ID : ALT_ITEM_LIST_ID;
  const name = `${isFavorite ? FAVORITE_OOS_NAME : ALT_ITEM_LIST_ID} - ${outOfStockProduct.name}`;

  dispatchListItemTrackingEvent({
    itemListId: id,
    itemListName: name,
    products: alternativeProducts,
    currency,
    isDiscoveryOffer,
    trackingPosition,
    raisedBy
  });
}

export function trackProductClick(
  product: Product,
  trackingPosition: number,
  isDiscoveryOffer: boolean,
  currency: string,
  itemListName: string,
  clickLocation = 'plp product card',
  eventRaisedBy?: string,
  itemListId?: string
) {
  dispatchTrackingEvent({
    event: 'select_item',
    click_location: clickLocation,
    event_raised_by: eventRaisedBy || RAISED_BY,
    subscription_product_included: hasSubscriptions(product) ? 'true' : 'false',
    discovery_offer_included: isDiscoveryOffer ? 'true' : 'false',
    ecommerce: {
      currency,
      value: product.pricing.price / PRICE_REPRESENTATION_MULTIPLIER,
      items: [
        productToGtmItemListPayload(
          product,
          isDiscoveryOffer,
          trackingPosition,
          itemListId || RAISED_BY,
          itemListName || RAISED_BY
        )
      ]
    }
  });
}

type DispatchListItemTrackingEventProps = {
  currency: string;
  itemListId: string;
  itemListName: string;
  products: Product[];
  isDiscoveryOffer: boolean;
  trackingPosition: number;
  raisedBy?: string;
};

const dispatchListItemTrackingEvent = ({
  currency,
  itemListId,
  itemListName,
  products,
  isDiscoveryOffer,
  trackingPosition,
  raisedBy = RAISED_BY
}: DispatchListItemTrackingEventProps) => {
  dispatchTrackingEvent({
    event: 'view_item_list',
    event_raised_by: raisedBy,
    subscription_product_included: products.some(hasSubscriptions)
      ? 'true'
      : 'false',
    discovery_offer_included: isDiscoveryOffer ? 'true' : 'false',
    ecommerce: {
      currency,
      value:
        products.reduce((total, p) => total + p.pricing.price, 0) /
        PRICE_REPRESENTATION_MULTIPLIER,
      items: products.map((product, idx) =>
        productToGtmItemListPayload(
          product,
          isDiscoveryOffer,
          trackingPosition + idx,
          itemListId,
          itemListId === FAVORITE_LIST
            ? `wishlist - ${product.type}`
            : itemListName
        )
      )
    }
  });
};

function normalizeFilterValuesString(values: string) {
  return values
    .replaceAll(new RegExp(`^(${PREFIXES.join('|')})`, 'g'), '')
    .slice(0, MAX_TRACKING_LENGTH);
}

function normalizeFilterValues(values: FilterableValue[]): string {
  if (values.length > 1 && values.every((value) => typeof value === 'number')) {
    return `${Math.min(...values)}-${Math.max(...values)}`;
  }

  // Currently we are already passing the category as a filterType, so even if we remove
  // the prefix, they are still unique values because they relate to the filterType:
  // Example:
  //  filter_type: 'cupSizes|collections|aromatic',
  //  filter_values: 'lungo|ispirazione-italiana|hazelnut',
  return normalizeFilterValuesString(values.join('-'));
}

function normalizeFilterTypeAndValues(
  filterType: string,
  filterValues: string
): {
  filter_type: string;
  filter_values: string;
} {
  return {
    filter_type: filterType.slice(0, MAX_TRACKING_LENGTH),
    filter_values: normalizeFilterValuesString(filterValues)
  };
}
